/**
 * Domain Enums (admin-ui owned)
 *
 * These mirror core-platform's auth domain enums (see
 * `packages/core-platform/src/auth/types.ts`) member-for-member, with identical
 * string values. They are intentionally defined here — frontend-owned — instead
 * of being value-imported from `@lognai/plugin-sdk`.
 *
 * WHY: `@lognai/core-platform` ships CommonJS only (no ESM build, no
 * `module`/`exports` ESM entry). The `@lognai/plugin-sdk` facade re-exports
 * these enums as runtime VALUES from core-platform. That resolves fine for tsc
 * and for backend plugins (CJS at runtime), but the web-app's bundler
 * (Vite/rollup, strict ESM) cannot resolve a named value export through
 * core-platform's CJS and fails the bundle. Owning the runtime enums locally
 * keeps the value-imports inside ESM-bundleable code.
 *
 * Domain TYPES (Company, CompanyPartnership, AccessObject, etc.) are erased at
 * build time, so they remain safe to import as `type` from their original
 * sources and are NOT duplicated here.
 */

/**
 * Company type enumeration
 */
export enum CompanyType {
  /** Primary enterprise company that can have partners */
  PRIMARY_ENTERPRISE = 'PRIMARY_ENTERPRISE',
  /** Partner company that collaborates with enterprises */
  PARTNER = 'PARTNER',
}

/**
 * Company status enumeration
 */
export enum CompanyStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
  PENDING_VERIFICATION = 'PENDING_VERIFICATION',
}

/**
 * User status enumeration
 */
export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  PENDING = 'pending',
}

/**
 * Partnership status enumeration
 */
export enum PartnershipStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  TERMINATED = 'TERMINATED',
}

/**
 * Partner visibility enumeration
 */
export enum PartnerVisibility {
  /** Full visibility of shared data */
  FULL = 'FULL',
  /** Limited visibility (filtered fields) */
  LIMITED = 'LIMITED',
  /** Highly restricted visibility */
  RESTRICTED = 'RESTRICTED',
}

/**
 * Partner access level enumeration
 */
export enum PartnerAccessLevel {
  /** No access — the relationship exists but grants no permissions (default-deny). */
  NONE = 'NONE',
  /** Read-only access */
  READ_ONLY = 'READ_ONLY',
  /** Read and write access */
  READ_WRITE = 'READ_WRITE',
  /** Full access including delete */
  FULL = 'FULL',
}
